import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, FileText, Film, Mic, Image, File, HardDrive, Search, X, ArrowLeft, Trash2, ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_TYPE_ICONS = {
  pdf: <FileText className="w-5 h-5 text-red-500" />,
  video: <Film className="w-5 h-5 text-blue-500" />,
  audio: <Mic className="w-5 h-5 text-purple-500" />,
  image: <Image className="w-5 h-5 text-emerald-500" />,
  other: <File className="w-5 h-5 text-slate-400" />,
};

function getFileCategory(filename) {
  if (!filename) return 'other';
  if (filename.startsWith('recording_') || filename.startsWith('feedback_')) return 'audio';
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'pptx', 'txt', 'csv'].includes(ext)) return 'pdf';
  if (['mp4', 'webm', 'avi', 'mov', 'mkv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'flac'].includes(ext)) return 'audio';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'other';
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return dateStr; }
}

export default function FileManagerPanel({ onBack }) {
  const { user, token } = useAuth();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin = user?.role === 'admin';

  const [files, setFiles] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({ total_files: 0, total_size: 0 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterClass, setFilterClass] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteHard, setDeleteHard] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Storage health (admin only)
  const [storageStatus, setStorageStatus] = useState(null);
  const [deploySafety, setDeploySafety] = useState(null);
  const [healthReport, setHealthReport] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [reindexResult, setReindexResult] = useState(null);
  const [runningIntegrity, setRunningIntegrity] = useState(false);

  const refreshStorageStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/files/storage-status`, headers);
      setStorageStatus(res.data);
    } catch {
      // silent — banner is best-effort
    }
    try {
      const safety = await axios.get(`${API}/files/deploy-safety`, headers);
      setDeploySafety(safety.data);
    } catch {
      // silent
    }
  }, [isAdmin, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const runIntegrityNow = async () => {
    setRunningIntegrity(true);
    try {
      await axios.post(`${API}/files/admin/run-integrity-check`, {}, headers);
      toast.success('Verificación ejecutada. Revisa tus notificaciones.');
      await refreshStorageStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar verificación');
    }
    setRunningIntegrity(false);
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/files/admin/health-check`, headers);
      setHealthReport(res.data);
      const c = res.data.counts || {};
      if ((c.local_only || 0) + (c.broken || 0) + (c.orphans || 0) === 0) {
        toast.success('Integridad verificada: todos los archivos están en la nube.');
      } else {
        const stale = c.broken_stale || 0;
        const real = c.broken_referenced || 0;
        toast.warning(`${c.local_only || 0} locales, ${real} rotos activos, ${stale} fantasmas, ${c.orphans || 0} huérfanos.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al verificar integridad');
    }
    setHealthLoading(false);
  };

  const [purging, setPurging] = useState(false);
  const runPurgeStale = async () => {
    const stale = healthReport?.counts?.broken_stale || 0;
    if (stale === 0) {
      toast.info('No hay registros fantasma para limpiar.');
      return;
    }
    const ok = window.confirm(
      `Se eliminarán ${stale} registros fantasma de archivos rotos sin uso activo.\n\n` +
      `Esta acción NO toca los ${healthReport?.counts?.local_only || 0} archivos locales ` +
      `ni los ${healthReport?.counts?.cloud_ok || 0} archivos en la nube.\n\n¿Continuar?`
    );
    if (!ok) return;
    setPurging(true);
    try {
      const res = await axios.post(`${API}/files/admin/purge-stale-broken`, {}, headers);
      toast.success(`${res.data.deleted} registro(s) fantasma eliminado(s).`);
      await runHealthCheck();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al limpiar registros fantasma');
    }
    setPurging(false);
  };

  const [purgingObsolete, setPurgingObsolete] = useState(false);
  const runPurgeObsolete = async () => {
    // Preview the impact via dry_run first
    let preview;
    try {
      preview = (await axios.post(`${API}/files/admin/purge-obsolete?dry_run=true`, {}, headers)).data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al previsualizar');
      return;
    }
    const wd = preview.would_delete || {};
    if ((wd.total || 0) === 0) {
      toast.info('No hay registros obsoletos para eliminar.');
      return;
    }
    const ok = window.confirm(
      `Se eliminarán ${wd.total} registros obsoletos (sin referencia activa):\n` +
      `  • ${wd.cloud || 0} archivos en la nube (libera espacio)\n` +
      `  • ${wd.local_ghosts || 0} registros locales fantasma (el archivo físico ya no existe)\n` +
      `  • ${wd.broken || 0} registros fantasma\n\n` +
      `SE PRESERVAN sin tocar:\n` +
      `  • ${preview.preserved_active} archivos activos (referenciados desde contenido)\n\n` +
      `¿Continuar con la eliminación?`
    );
    if (!ok) return;
    setPurgingObsolete(true);
    try {
      const res = await axios.post(`${API}/files/admin/purge-obsolete`, {}, headers);
      const d = res.data.deleted || {};
      const freedMB = ((res.data.freed_bytes || 0) / (1024 * 1024)).toFixed(1);
      const failedCloud = res.data.failed_cloud_count || 0;
      // iter71.10 · Show the REAL failure reasons (402/403/network) —
      // no more «falló por créditos» as a catch-all. `failed_cloud_deletes`
      // now includes the `reason` field per entry.
      if (failedCloud > 0) {
        const reasonsMap = {};
        (res.data.failed_cloud_deletes || []).forEach(f => {
          const r = (f.reason || 'desconocido').replace(/_/g, ' ');
          reasonsMap[r] = (reasonsMap[r] || 0) + 1;
        });
        const reasonsSummary = Object.entries(reasonsMap)
          .map(([r, n]) => `${n} · ${r}`)
          .join(', ');
        toast.warning(
          `${d.total} registros eliminados de la BD (${freedMB} MB liberados). ` +
          `${failedCloud} no pudieron borrar el archivo en la nube: ${reasonsSummary}.`
        );
      } else {
        toast.success(`${d.total} obsoletos eliminados (${freedMB} MB liberados).`);
      }
      await runHealthCheck();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al limpiar obsoletos');
    }
    setPurgingObsolete(false);
  };

  const runReindex = async () => {
    setReindexing(true);
    try {
      const res = await axios.post(`${API}/files/admin/reindex`, {}, headers);
      const s = res.data.summary || {};
      if (s.skipped) {
        toast.warning('Nube no disponible — recarga créditos y reintenta.');
      } else {
        const migrated = s.migrated || 0;
        const missing = s.missing || 0;
        const failed = s.failed || 0;
        const fails = s.failures || [];
        if (migrated > 0) {
          toast.success(`Migrados ${migrated} archivos a la nube.`);
        } else if (fails.length > 0) {
          // Build a detailed error message from the first 2 failures
          const reasons = [...new Set(fails.map(f => f.reason))].slice(0, 3).join(', ');
          const sampleDetail = fails[0]?.detail?.slice(0, 80) || '';
          toast.error(
            `Migración falló: ${failed} con error de nube, ${missing} sin archivo en disco. ` +
            `Razón principal: ${reasons}. ${sampleDetail ? '"' + sampleDetail + '"' : ''}`,
            { duration: 10000 }
          );
        } else {
          toast.info('No hay archivos locales pendientes de migrar.');
        }
        setReindexResult(res.data.summary);
        await refreshStorageStatus();
        if (healthReport) await runHealthCheck();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reindexar');
    }
    setReindexing(false);
  };

  const [pingingCloud, setPingingCloud] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const runPingCloud = async () => {
    setPingingCloud(true);
    setPingResult(null);
    try {
      const res = await axios.get(`${API}/files/admin/ping-cloud`, headers);
      setPingResult(res.data);
      if (res.data.ok) {
        toast.success('Cloud OK: las escrituras funcionan correctamente.');
      } else {
        toast.error(
          `Cloud FALLA en ${res.data.stage}: HTTP ${res.data.status}. ` +
          `Respuesta: "${(res.data.body || '').slice(0, 120)}"`,
          { duration: 15000 }
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar ping');
    }
    setPingingCloud(false);
  };

  // Restore / inspect file by filename — recovery for accidentally deleted files
  const [restoreInput, setRestoreInput] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);

  const runInspect = async () => {
    const fn = restoreInput.trim();
    if (!fn) return;
    setRestoring(true);
    try {
      const res = await axios.get(`${API}/files/admin/inspect/${encodeURIComponent(fn)}`, headers);
      setInspectResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al inspeccionar');
    }
    setRestoring(false);
  };

  const runRestore = async () => {
    const fn = restoreInput.trim();
    if (!fn) return;
    if (!window.confirm(
      `¿Restaurar el archivo ${fn}?\n\n` +
      `Esto re-habilita el acceso (is_deleted=false). ` +
      `El archivo físico debe seguir existiendo en cloud/local.`
    )) return;
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/files/admin/restore/${encodeURIComponent(fn)}`, {}, headers);
      const r = res.data;
      toast.success(
        `Restaurado: ${r.filename} ` +
        `(estaba is_deleted=${r.was_deleted}). ` +
        `Usado en: ${r.referenced_in.join(', ') || '—'}`
      );
      setInspectResult(null);
      setRestoreInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar');
    }
    setRestoring(false);
  };

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterClass !== 'all') params.class_id = filterClass;
      if (filterType !== 'all') params.file_type = filterType;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await axios.get(`${API}/files/admin/list`, { ...headers, params });
      setFiles(res.data.files || []);
      setStats(res.data.stats || { total_files: 0, total_size: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar archivos');
    }
    setLoading(false);
  }, [token, filterClass, filterType, searchQuery]);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/classes`, headers);
      setClasses(res.data);
    } catch (err) { console.error("[FileManagerPanel] silent error:", err); }
  }, [token]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { fetchFiles(); }, [fetchFiles]);
  useEffect(() => { refreshStorageStatus(); }, [refreshStorageStatus]);

  const clearFilters = () => {
    setFilterClass('all');
    setFilterType('all');
    setSearchQuery('');
  };

  const handleDelete = async (force = false, hard = false) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      if (force) params.set('force', 'true');
      if (hard) params.set('hard', 'true');
      const q = params.toString();
      const url = `${API}/files/admin/delete/${deleteTarget.filename}${q ? '?' + q : ''}`;
      const res = await axios.delete(url, headers);
      const cascaded = res.data.cascaded_tutorials || 0;
      toast.success(
        cascaded > 0
          ? `Archivo eliminado ${hard ? 'definitivamente' : ''} (también ${cascaded} tutorial(es) limpiados).`
          : `Archivo eliminado ${hard ? 'definitivamente' : 'correctamente'}`
      );
      setDeleteTarget(null);
      setDeleteHard(false);
      fetchFiles();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returned a structured 409 when file is used by a tutorial
      if (err.response?.status === 409 && detail?.code === 'file_used_by_tutorial') {
        const titles = (detail.tutorials || []).map(t => `• ${t.titulo}`).join('\n');
        const confirmed = window.confirm(
          `⚠️ Este archivo está en uso por ${detail.tutorials?.length || 0} tutorial(es):\n\n${titles}\n\n` +
          `Si continúas, los tutoriales perderán su archivo (la card quedará vacía).\n\n` +
          `¿Borrar de todas formas?`
        );
        if (confirmed) {
          setDeleting(false);
          return handleDelete(true, hard);
        }
      } else {
        // iter71.10 · Surface the REAL detail (cloud status, credit
        // depleted, auth error, HTTP code) — no more generic
        // «falló por créditos».
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error al eliminar archivo'));
      }
    }
    setDeleting(false);
  };

  const hasFilters = filterClass !== 'all' || filterType !== 'all' || searchQuery.trim();

  return (
    <div data-testid="file-manager-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button data-testid="file-manager-back-btn" variant="ghost" size="sm" onClick={onBack} className="rounded-xl gap-1">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Montserrat' }}>
              Gestión de Archivos
            </h2>
            <p className="text-sm text-slate-500">
              {isAdmin ? 'Todos los archivos de la plataforma' : 'Archivos de tus clases'}
            </p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="file-health-check-btn"
              variant="outline"
              size="sm"
              onClick={runHealthCheck}
              disabled={healthLoading}
              className="rounded-xl gap-1"
            >
              <ShieldCheck className="w-4 h-4" /> {healthLoading ? 'Verificando…' : 'Verificar integridad'}
            </Button>
            <Button
              data-testid="file-reindex-btn"
              variant="outline"
              size="sm"
              onClick={runReindex}
              disabled={reindexing}
              className="rounded-xl gap-1"
            >
              <RefreshCw className={`w-4 h-4 ${reindexing ? 'animate-spin' : ''}`} /> {reindexing ? 'Reindexando…' : 'Reindexar a la nube'}
            </Button>
            <Button
              data-testid="file-ping-cloud-btn"
              variant="ghost"
              size="sm"
              onClick={runPingCloud}
              disabled={pingingCloud}
              className="rounded-xl gap-1 text-purple-700"
              title="Hace un PUT real a la API de almacenamiento para verificar que las escrituras funcionan"
            >
              <HardDrive className={`w-4 h-4 ${pingingCloud ? 'animate-pulse' : ''}`} /> {pingingCloud ? 'Probando…' : 'Test cloud (PUT)'}
            </Button>
            <Button
              data-testid="file-run-integrity-btn"
              variant="ghost"
              size="sm"
              onClick={runIntegrityNow}
              disabled={runningIntegrity}
              className="rounded-xl gap-1 text-slate-600"
              title="Ejecuta el chequeo diario ahora y crea notificaciones si hay anomalías"
            >
              <ShieldCheck className={`w-4 h-4 ${runningIntegrity ? 'animate-pulse' : ''}`} /> {runningIntegrity ? 'Ejecutando…' : 'Ejecutar verificación ahora'}
            </Button>
            {healthReport && (healthReport.counts?.broken_stale || 0) > 0 && (
              <Button
                data-testid="file-purge-stale-btn"
                variant="outline"
                size="sm"
                onClick={runPurgeStale}
                disabled={purging}
                className="rounded-xl gap-1 border-red-200 text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> {purging ? 'Limpiando…' : `Limpiar ${healthReport.counts.broken_stale} fantasma(s)`}
              </Button>
            )}
            {healthReport && (healthReport.counts?.obsolete || 0) > 0 && (
              <Button
                data-testid="file-purge-obsolete-btn"
                variant="outline"
                size="sm"
                onClick={runPurgeObsolete}
                disabled={purgingObsolete}
                className="rounded-xl gap-1 border-amber-300 text-amber-800 hover:bg-amber-50"
                title="Elimina registros sin referencia activa. NUNCA toca locales ni activos."
              >
                <Trash2 className="w-4 h-4" /> {purgingObsolete ? 'Limpiando…' : `Limpiar ${healthReport.counts.obsolete - (healthReport.counts.obsolete_local || 0)} obsoletos`}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* DO-NOT-REDEPLOY hard banner (only when deploy is unsafe) */}
      {isAdmin && deploySafety && !deploySafety.safe_to_deploy && deploySafety.warnings.length > 0 && (
        <div data-testid="deploy-safety-banner" className="mb-4 p-4 rounded-xl border-2 border-red-500 bg-red-50 text-red-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="font-bold text-base mb-1">🚫 NO REDESPLEGAR la aplicación todavía</p>
              <ul className="text-sm space-y-1">
                {deploySafety.warnings.map((w, i) => (
                  <li key={`${w.code}-${i}`}>• {w.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Storage health banners (admin only) */}
      {isAdmin && storageStatus && !storageStatus.cloud_available && (
        <div data-testid="storage-banner-cloud-down" className="mb-4 p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Almacenamiento en la nube no disponible</p>
            <p className="text-red-600/80">Los nuevos archivos se guardarán temporalmente en disco local y se migrarán automáticamente cuando se restablezca el servicio.</p>
          </div>
        </div>
      )}
      {isAdmin && storageStatus && storageStatus.cloud_available && storageStatus.write_degraded && (
        <div data-testid="storage-banner-write-degraded" className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Subidas a la nube degradadas</p>
            <p className="text-amber-700/80">
              Último error: <code className="text-xs">{storageStatus.last_write_failure_reason}</code>.
              {storageStatus.last_write_failure_reason === 'credit_depleted_402' && ' Recarga créditos para restaurar el servicio.'}
            </p>
          </div>
        </div>
      )}
      {isAdmin && healthReport && (
        <div data-testid="storage-health-report" className="mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50 text-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-slate-700 flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Reporte de integridad</p>
            <button onClick={() => setHealthReport(null)} className="text-slate-400 hover:text-slate-600" aria-label="cerrar"><X className="w-4 h-4" /></button>
          </div>

          {/* Active vs Obsolete summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div data-testid="report-active-card" className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Archivos activos</p>
              <p className="text-2xl font-bold text-emerald-800">{healthReport.counts.active || 0}</p>
              <p className="text-xs text-emerald-600">Referenciados desde contenido — NUNCA se eliminan</p>
            </div>
            <div data-testid="report-obsolete-card" className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Archivos obsoletos</p>
              <p className="text-2xl font-bold text-amber-800">{healthReport.counts.obsolete || 0}</p>
              <p className="text-xs text-amber-600">
                Sin referencia ·
                {' '}{healthReport.counts.obsolete_cloud || 0} nube +
                {' '}{healthReport.counts.obsolete_broken || 0} fantasma +
                {' '}<strong>{healthReport.counts.obsolete_local || 0} local (protegidos)</strong>
              </p>
              {(healthReport.counts.obsolete_size_bytes || 0) > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  Espacio recuperable: <strong>{(healthReport.counts.obsolete_size_bytes / (1024 * 1024)).toFixed(1)} MB</strong>
                </p>
              )}
            </div>
          </div>

          {/* Detailed counts by storage */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
            <div><span className="font-bold text-emerald-700">{healthReport.counts.cloud_ok}</span> en la nube</div>
            <div><span className="font-bold text-amber-700">{healthReport.counts.local_only}</span> solo en disco</div>
            <div><span className="font-bold text-red-700">{healthReport.counts.broken_referenced || 0}</span> rotos activos</div>
            <div title="Registros fantasma: archivos ya borrados que no están referenciados desde ningún contenido. Se pueden limpiar de forma segura."><span className="font-bold text-slate-500">{healthReport.counts.broken_stale || 0}</span> fantasmas</div>
            <div><span className="font-bold text-slate-700">{healthReport.counts.orphans}</span> huérfanos</div>
            <div><span className="font-bold text-slate-700">{healthReport.counts.referenced_files}</span> referenciados</div>
          </div>
          {(healthReport.counts.local_only > 0 || (healthReport.counts.broken_stale || 0) > 0 || (healthReport.counts.broken_referenced || 0) > 0 || (healthReport.counts.obsolete_cloud || 0) > 0) && (
            <div className="text-xs text-slate-500 mt-2 space-y-1">
              {healthReport.counts.local_only > 0 && (
                <p>• <strong>Reindexar a la nube</strong> migra los {healthReport.counts.local_only} archivos solo-locales (necesita créditos).</p>
              )}
              {(healthReport.counts.obsolete_cloud || 0) > 0 && (
                <p>• <strong>Limpiar obsoletos</strong> libera espacio borrando {healthReport.counts.obsolete_cloud} archivos en nube sin referencia.</p>
              )}
              {(healthReport.counts.broken_stale || 0) > 0 && (
                <p>• <strong>Limpiar fantasmas</strong> elimina {healthReport.counts.broken_stale} registros sin uso activo.</p>
              )}
              {(healthReport.counts.broken_referenced || 0) > 0 && (
                <p className="text-red-600">• <strong>{healthReport.counts.broken_referenced} rotos activos</strong>: aún referenciados en contenidos; requieren re-subida por el docente.</p>
              )}
              <p className="text-emerald-700">• <strong>Política de seguridad</strong>: los archivos con <code>storage=local</code> NUNCA se eliminan por purga automática, aunque aparezcan como obsoletos.</p>
            </div>
          )}
        </div>
      )}

      {/* Cloud PUT ping result (admin diagnostic) */}
      {isAdmin && pingResult && (
        <div data-testid="ping-cloud-result" className={`mb-4 p-3 rounded-xl border text-sm ${pingResult.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-300 bg-red-50 text-red-800'}`}>
          <div className="flex items-center justify-between mb-1">
            <p className="font-medium flex items-center gap-1">
              <HardDrive className="w-4 h-4" /> Test directo de escritura en la nube
            </p>
            <button onClick={() => setPingResult(null)} className="opacity-60 hover:opacity-100" aria-label="cerrar"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs">
            Resultado: <strong>{pingResult.ok ? '✅ OK' : '❌ FALLO'}</strong> ·
            Etapa: <code>{pingResult.stage}</code> ·
            HTTP <code>{pingResult.status}</code>
          </p>
          {pingResult.body && (
            <p className="text-xs mt-1 font-mono bg-white/60 p-2 rounded break-all">
              {pingResult.body}
            </p>
          )}
        </div>
      )}

      {/* Reindex result with detailed failures */}
      {isAdmin && reindexResult && reindexResult.failures && reindexResult.failures.length > 0 && (
        <div data-testid="reindex-result" className="mb-4 p-3 rounded-xl border border-amber-300 bg-amber-50 text-sm text-amber-900">
          <div className="flex items-center justify-between mb-1">
            <p className="font-medium flex items-center gap-1">
              <RefreshCw className="w-4 h-4" /> Resultado de la migración
            </p>
            <button onClick={() => setReindexResult(null)} className="opacity-60 hover:opacity-100" aria-label="cerrar"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs mb-2">
            Migrados: <strong>{reindexResult.migrated}</strong> ·
            Sin archivo en disco: <strong>{reindexResult.missing}</strong> ·
            Error de nube: <strong>{reindexResult.failed}</strong>
          </p>
          <details>
            <summary className="cursor-pointer text-xs font-medium">Ver razones por archivo ({reindexResult.failures.length})</summary>
            <ul className="text-xs mt-2 space-y-1 max-h-48 overflow-auto">
              {reindexResult.failures.map((f, i) => (
                <li key={`${f.filename}-${i}`} className="font-mono bg-white/60 p-1 rounded">
                  <strong>{f.original_name || f.filename}</strong>: {f.reason}
                  {f.detail && <div className="text-[10px] opacity-80 break-all">{f.detail}</div>}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* Restore / Inspect file by filename (admin recovery tool) */}
      {isAdmin && (
        <div data-testid="file-restore-panel" className="mb-4 p-3 rounded-xl border border-blue-200 bg-blue-50 text-sm">
          <p className="font-medium text-blue-800 mb-2 flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Restaurar / Inspeccionar archivo por filename
          </p>
          <p className="text-xs text-blue-700 mb-2">
            Si un video o PDF retorna 404 (p. ej. tras borrar por error), pega aquí el filename
            (formato UUID, ej. <code>7b108918-758d-404c-9458-e7c20a54c2c1.mp4</code>) e
            inspecciona o restaura el acceso.
          </p>
          <div className="flex flex-wrap gap-2">
            <Input
              data-testid="restore-filename-input"
              value={restoreInput}
              onChange={(e) => setRestoreInput(e.target.value)}
              placeholder="uuid.ext"
              className="flex-1 min-w-[260px] rounded-xl"
            />
            <Button
              data-testid="inspect-file-btn"
              variant="outline"
              size="sm"
              onClick={runInspect}
              disabled={restoring || !restoreInput.trim()}
              className="rounded-xl gap-1"
            >
              <Search className="w-4 h-4" /> Inspeccionar
            </Button>
            <Button
              data-testid="restore-file-btn"
              size="sm"
              onClick={runRestore}
              disabled={restoring || !restoreInput.trim()}
              className="rounded-xl gap-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RefreshCw className={`w-4 h-4 ${restoring ? 'animate-spin' : ''}`} /> Restaurar
            </Button>
          </div>
          {inspectResult && (
            <div data-testid="inspect-result" className="mt-3 p-2 rounded-lg bg-white border border-blue-100 text-xs space-y-1 max-h-64 overflow-auto">
              <p><strong>file_ref existe:</strong> {inspectResult.summary.ref_exists ? '✅ sí' : '❌ no'}</p>
              <p><strong>is_deleted:</strong> {inspectResult.summary.is_deleted ? '⚠️ true (bloqueado)' : '✅ false'}</p>
              <p><strong>storage:</strong> {inspectResult.summary.storage || '—'}</p>
              <p><strong>Referenciado en:</strong></p>
              <ul className="ml-4 list-disc text-slate-600">
                {Object.entries(inspectResult.referenced_in).map(([k, v]) =>
                  v.length > 0 ? (
                    <li key={k}>
                      <strong>{k}</strong>: {v.map(x => x.titulo || x.title || x.id).join(', ')}
                    </li>
                  ) : null
                )}
                {inspectResult.summary.total_refs === 0 && <li>— sin referencias activas</li>}
              </ul>
              {inspectResult.summary.is_deleted && inspectResult.summary.total_refs > 0 && (
                <p className="text-amber-700 mt-2">
                  ⚠️ Este archivo está marcado como borrado PERO aún tiene referencias.
                  Probablemente fue borrado por error — pulsa <strong>Restaurar</strong>.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Card className="rounded-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <File className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_files}</p>
              <p className="text-xs text-slate-500">Archivos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50">
              <HardDrive className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{formatSize(stats.total_size)}</p>
              <p className="text-xs text-slate-500">Espacio usado</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50">
              <FileText className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.documents || 0}</p>
              <p className="text-xs text-slate-500">Documentos</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Film className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.media || 0}</p>
              <p className="text-xs text-slate-500">Multimedia</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="file-search-input"
            placeholder="Buscar por nombre..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger data-testid="file-filter-class" className="w-[200px] rounded-xl">
            <SelectValue placeholder="Filtrar por clase" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las clases</SelectItem>
            {classes.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger data-testid="file-filter-type" className="w-[180px] rounded-xl">
            <SelectValue placeholder="Tipo de archivo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="document">Documentos</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="audio">Audio</SelectItem>
            <SelectItem value="image">Imágenes</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button data-testid="file-clear-filters" variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-slate-500">
            <X className="w-3 h-3" /> Limpiar
          </Button>
        )}
      </div>

      {/* Files Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Cargando archivos...</div>
        ) : files.length === 0 ? (
          <div className="text-center py-12">
            <File className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-400">{hasFilters ? 'No se encontraron archivos con estos filtros' : 'No hay archivos aún'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="files-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Archivo</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Clase</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Subido por</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Tipo</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Tamaño</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Fecha</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Ubicación</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, i) => {
                  const cat = getFileCategory(f.filename);
                  return (
                    <tr key={`${f.filename || 'unknown'}-${f.source || 'src'}-${i}`} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`file-row-${i}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {FILE_TYPE_ICONS[cat] || FILE_TYPE_ICONS.other}
                          <span className="font-medium text-slate-700 truncate max-w-[200px]" title={f.original_name || f.filename}>
                            {f.original_name || f.filename}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{f.class_name || '-'}</td>
                      <td className="px-4 py-3 text-slate-500">{f.uploaded_by_name || '-'}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs capitalize">{cat}</Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatSize(f.size)}</td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">{formatDate(f.created_at)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={f.storage === 'cloud' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-slate-50 text-slate-500'}>
                          {f.storage === 'cloud' ? 'Nube' : 'Local'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={`${API}/files/${f.filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs font-medium"
                            data-testid={`file-download-${i}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </a>
                          <button
                            onClick={() => { setDeleteHard(false); setDeleteTarget(f); }}
                            className="inline-flex items-center gap-1 text-red-400 hover:text-red-600 text-xs font-medium"
                            title="Eliminar (soft-delete, recuperable)"
                            data-testid={`file-delete-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setDeleteHard(true); setDeleteTarget(f); }}
                            className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 text-xs font-medium"
                            title="Eliminar definitivamente el registro"
                            data-testid={`file-hard-delete-${i}`}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteHard(false); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteHard ? '¿Eliminar definitivamente?' : '¿Eliminar archivo?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteHard ? (
                <>Se eliminará <strong>definitivamente</strong> el registro de la base de datos y el objeto en la nube (si aún existe) para <strong className="text-slate-700">{deleteTarget?.original_name || deleteTarget?.filename}</strong>. No hay opción de recuperar.</>
              ) : (
                <>Se marcará como eliminado <strong className="text-slate-700">{deleteTarget?.original_name || deleteTarget?.filename}</strong> de la clase <strong className="text-slate-700">{deleteTarget?.class_name}</strong>. Podrá recuperarse desde el registro de la BD hasta el próximo purge.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="file-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="file-delete-confirm"
              onClick={() => handleDelete(false, deleteHard)}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Eliminando...' : (deleteHard ? 'Eliminar definitivamente' : 'Eliminar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
